import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtInt } from "@/lib/api";
import { PageHeader, Section, Pill, EmptyState } from "@/components/ui-kit";
import { Modal } from "./Books";
import { Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "entrada_impresion", label: "Entrada desde imprenta", from: false, to: true, tone: "success" },
  { value: "envio_deposito",    label: "Envío a depósito",       from: true,  to: true,  tone: "brand" },
  { value: "venta_directa",     label: "Venta directa",          from: true,  to: false, tone: "brand" },
  { value: "venta_feria",       label: "Venta en feria",         from: true,  to: false, tone: "amber" },
  { value: "devolucion",        label: "Devolución",             from: false, to: true,  tone: "warning" },
  { value: "deterioro",         label: "Deterioro / promocional", from: true, to: false, tone: "danger" },
  { value: "traslado",          label: "Traslado entre almacenes", from: true, to: true, tone: "default" },
];

const emptyMov = { book_id: "", type: "entrada_impresion", from_warehouse_id: "", to_warehouse_id: "", quantity: 1, unit_price: 0, reference: "", notes: "" };

export default function Movements() {
  const [items, setItems] = useState([]);
  const [books, setBooks] = useState([]);
  const [whs, setWhs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyMov);

  async function load() {
    const [m, b, w] = await Promise.all([
      api.get("/movements"), api.get("/books"), api.get("/warehouses"),
    ]);
    setItems(m.data); setBooks(b.data); setWhs(w.data);
  }
  useEffect(() => { load(); }, []);

  const typeDef = TYPES.find(t => t.value === form.type) || TYPES[0];

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 0,
        unit_price: parseFloat(form.unit_price) || 0,
        from_warehouse_id: typeDef.from ? form.from_warehouse_id : null,
        to_warehouse_id: typeDef.to ? form.to_warehouse_id : null,
      };
      await api.post("/movements", payload);
      toast.success("Movimiento registrado");
      setShowForm(false); setForm(emptyMov); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar");
    }
  }

  const bookById = Object.fromEntries(books.map(b => [b.id, b]));
  const whById = Object.fromEntries(whs.map(w => [w.id, w]));

  return (
    <div data-testid="movements-page">
      <PageHeader
        title="Movimientos de stock"
        subtitle="Cada movimiento actualiza el stock consolidado. No se pueden editar, sólo añadir o anular."
        actions={
          <button onClick={() => { setForm(emptyMov); setShowForm(true); }}
            data-testid="new-movement-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2 transition-colors">
            <Plus size={16} strokeWidth={1.5} /> Nuevo movimiento
          </button>
        }
      />

      <Section>
        {items.length === 0 ? (
          <EmptyState title="Sin movimientos" description="Registra la primera entrada desde imprenta para empezar a controlar tu stock." testid="movements-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                  <th className="text-left px-5 py-3 font-medium">Fecha</th>
                  <th className="text-left px-5 py-3 font-medium">Tipo</th>
                  <th className="text-left px-5 py-3 font-medium">Libro</th>
                  <th className="text-left px-5 py-3 font-medium">Origen / Destino</th>
                  <th className="text-right px-5 py-3 font-medium">Cant.</th>
                  <th className="text-left px-5 py-3 font-medium">Ref.</th>
                </tr>
              </thead>
              <tbody>
                {items.map(m => {
                  const t = TYPES.find(x => x.value === m.type) || { label: m.type, tone: "muted" };
                  const b = bookById[m.book_id];
                  return (
                    <tr key={m.id} className="border-t border-line hover:bg-sidebar/30" data-testid={`movement-row-${m.id}`}>
                      <td className="px-5 py-3 font-mono text-xs text-ink2 whitespace-nowrap">{fmtDate(m.date)}</td>
                      <td className="px-5 py-3"><Pill tone={t.tone}>{t.label}</Pill></td>
                      <td className="px-5 py-3">
                        <div className="truncate max-w-[220px]">{b?.title || "—"}</div>
                        <div className="text-[11px] font-mono text-ink2">{b?.isbn}</div>
                      </td>
                      <td className="px-5 py-3 text-xs text-ink2">
                        <div className="flex items-center gap-1">
                          <span>{whById[m.from_warehouse_id]?.name || "—"}</span>
                          <ArrowRight size={12} strokeWidth={1.2} />
                          <span>{whById[m.to_warehouse_id]?.name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right font-mono">{fmtInt(m.quantity)}</td>
                      <td className="px-5 py-3 text-xs text-ink2 font-mono">{m.reference || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {showForm && (
        <Modal title="Nuevo movimiento" onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="movement-form">
            <Field label="Tipo" required span={2}>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="movement-type" className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm">
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Libro" required span={2}>
              <select value={form.book_id} onChange={(e) => setForm({ ...form, book_id: e.target.value })}
                data-testid="movement-book" required className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm">
                <option value="">—</option>
                {books.map(b => <option key={b.id} value={b.id}>{b.title} · {b.isbn}</option>)}
              </select>
            </Field>
            {typeDef.from && (
              <Field label="Almacén origen" required>
                <select value={form.from_warehouse_id} onChange={(e) => setForm({ ...form, from_warehouse_id: e.target.value })}
                  required className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm">
                  <option value="">—</option>
                  {whs.map(w => <option key={w.id} value={w.id}>{w.name} ({w.type})</option>)}
                </select>
              </Field>
            )}
            {typeDef.to && (
              <Field label="Almacén destino" required>
                <select value={form.to_warehouse_id} onChange={(e) => setForm({ ...form, to_warehouse_id: e.target.value })}
                  required className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm">
                  <option value="">—</option>
                  {whs.map(w => <option key={w.id} value={w.id}>{w.name} ({w.type})</option>)}
                </select>
              </Field>
            )}
            <Field label="Cantidad" required>
              <input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="movement-qty" required className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm" />
            </Field>
            <Field label="PVP unitario (opcional)">
              <input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm" />
            </Field>
            <Field label="Referencia (factura / nota)" span={2}>
              <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm" />
            </Field>
            <Field label="Notas" span={2}>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm" />
            </Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
              <button type="submit" data-testid="movement-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Registrar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children, required, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}{required && <span className="text-brand"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
